import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { catchError, forkJoin, map, Observable, of, switchMap } from "rxjs";
import { User } from "../models/user.model";
import { IwUser } from "../models/iw-user.model";
import { Page } from "../models/page.model";
import { IwTeamMember } from "../models/iw-team-member.model";

@Injectable({
  providedIn: "root"
})
export class UserService {
  constructor(private http: HttpClient) {}

  public getUser(username: string | null): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      }),
      params: { username: username == null ? "" : username }
    };

    return this.http.get<User>(`${environment.fulcrumUrl}/api/v3/user`, httpOptions).pipe(
      catchError((error: any): Observable<User> => {
        if (error.status === 404) {
          console.log("Not Found");
          return of();
        } else if (error.status === 401) {
          console.log("Not Authorized");
          return of();
        }
        throw error;
      })
    );
  }

  /**
   * Retrieves the IW user corresponding to the email address
   * @param emailAddress the email address for which to retrieve the IW user
   */
  public getIwUser(emailAddress: string): Observable<Page<IwUser>> {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization:
          "Basic " + btoa(`${environment.claimItApiUsername}:${environment.claimItApiPassword}`)
      }),
      params: {
        "filter.email": emailAddress
      }
    };

    return this.http
      .get<Page<IwUser>>(`${environment.fulcrumUrl}/iw/api/v1/users`, httpOptions)
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Page<IwUser>> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          }
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Retrieves a page of users associated with an agency
   * @param agencyId the ID of the agency for which to retrieve the list page users
   * @param pageNumber the page number to retrieve
   */
  public getAgencyUsers(agencyId: number, pageNumber: number = 0): Observable<Page<IwTeamMember>> {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization:
          "Basic " + btoa(`${environment.claimItApiUsername}:${environment.claimItApiPassword}`)
      }),
      params: {
        page: pageNumber.toString()
      }
    };

    return this.http
      .get<
        Page<IwTeamMember>
      >(`${environment.fulcrumUrl}/iw/api/v1/agency/${agencyId}/users`, httpOptions)
      .pipe(
        catchError((error: HttpErrorResponse): Observable<Page<IwTeamMember>> => {
          if (error.status === 404) {
            console.log("Not Found");
            return of();
          }
          console.log(error);
          throw error;
        })
      );
  }

  /**
   * Retrieves a list of users associated with an agency
   * @param agencyId the ID of the agency for which to retrieve the list of users
   */
  public getAllAgencyUsersFromAllPages(agencyId: number): Observable<IwTeamMember[]> {
    return this.getAgencyUsers(agencyId, 0).pipe(
      switchMap((firstPage: Page<IwTeamMember>): Observable<IwTeamMember[]> => {
        const allUsers: IwTeamMember[] = [...firstPage.content];

        if (firstPage.page.totalPages <= 1) {
          // Only one page, return immediately
          return of(allUsers);
        }

        // Create observables for the remaining pages
        const remainingPageRequests: Observable<Page<IwTeamMember>>[] = [];
        for (let pageNumber: number = 1; pageNumber < firstPage.page.totalPages; pageNumber++) {
          remainingPageRequests.push(this.getAgencyUsers(agencyId, pageNumber));
        }

        // Execute all remaining requests in parallel
        return forkJoin(remainingPageRequests).pipe(
          map((remainingPages: Page<IwTeamMember>[]): IwTeamMember[] => {
            // Combine all users from all pages
            remainingPages.forEach((page: Page<IwTeamMember>): void => {
              allUsers.push(...page.content);
            });
            return allUsers;
          })
        );
      })
    );
  }
}
